import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Careers() {
  useEffect(() => {
    // Set page title and meta description
    document.title = '採用情報｜一緒に働く仲間を募集｜おかやま不動産販売株式会社';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'おかやま不動産販売株式会社の採用情報。不動産営業、事務スタッフを募集中。充実した研修制度と働きやすい環境で、あなたのキャリアをサポートします。');
    }

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "JobPosting",
          "title": "不動産営業スタッフ",
          "description": "お客様の不動産売買をサポートする営業職。未経験者歓迎、充実した研修制度あり。",
          "datePosted": "2024-01-01",
          "employmentType": "FULL_TIME",
          "hiringOrganization": {
            "@type": "Organization",
            "name": "おかやま不動産販売株式会社",
            "sameAs": import.meta.env.VITE_SITE_URL || 'https://example.com'
          },
          "jobLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "○○町1-2-3",
              "addressLocality": "岡山市北区",
              "addressRegion": "岡山県",
              "postalCode": "700-0000",
              "addressCountry": "JP"
            }
          },
          "baseSalary": {
            "@type": "MonetaryAmount",
            "currency": "JPY",
            "value": {
              "@type": "QuantitativeValue",
              "minValue": 250000,
              "maxValue": 400000,
              "unitText": "MONTH"
            }
          }
        },
        {
          "@type": "JobPosting",
          "title": "事務スタッフ",
          "description": "不動産取引に関する事務業務全般。丁寧な研修があるので未経験でも安心。",
          "datePosted": "2024-01-01",
          "employmentType": "FULL_TIME",
          "hiringOrganization": {
            "@type": "Organization",
            "name": "おかやま不動産販売株式会社",
            "sameAs": import.meta.env.VITE_SITE_URL || 'https://example.com'
          },
          "jobLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "○○町1-2-3",
              "addressLocality": "岡山市北区",
              "addressRegion": "岡山県",
              "postalCode": "700-0000",
              "addressCountry": "JP"
            }
          },
          "baseSalary": {
            "@type": "MonetaryAmount",
            "currency": "JPY",
            "value": {
              "@type": "QuantitativeValue",
              "minValue": 200000,
              "maxValue": 280000,
              "unitText": "MONTH"
            }
          }
        },
        {
          "@type": "WebPage",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/careers#webpage`,
          "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/careers`,
          "name": "採用情報｜おかやま不動産販売株式会社",
          "description": "おかやま不動産販売株式会社の採用情報と募集要項",
          "isPartOf": {
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`
          }
        }
      ]
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: '充実の給与体系',
      description: '基本給に加え、成果に応じたインセンティブ制度を導入しています。'
    },
    {
      icon: 'ri-time-line',
      title: 'ワークライフバランス',
      description: '残業時間の削減に取り組み、プライベートとの両立をサポートします。'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: '教育・研修制度',
      description: '業界未経験者でも安心の充実した研修プログラムをご用意しています。'
    },
    {
      icon: 'ri-team-line',
      title: 'チームワーク',
      description: '少数精鋭のチームで、一人ひとりが活躍できる環境です。'
    }
  ];

  const positions = [
    {
      title: '営業スタッフ',
      type: '正社員',
      salary: '月給25万円〜40万円（経験・能力により優遇）',
      requirements: [
        '普通自動車免許（AT限定可）',
        '宅地建物取引士資格（入社後取得可）',
        '不動産業界経験者優遇',
        'コミュニケーション能力の高い方'
      ],
      description: '不動産の売買仲介業務を担当していただきます。お客様との信頼関係を築き、最適な物件をご提案する重要な役割です。',
      image: 'https://public.readdy.ai/ai/img_res/0d4e883097090c4ba19167a8daceec9f.jpg'
    },
    {
      title: '事務スタッフ',
      type: '正社員・パート',
      salary: '月給20万円〜28万円（正社員）/ 時給1,000円〜1,200円（パート）',
      requirements: [
        'PC操作（Word、Excel）',
        '電話応対経験',
        '不動産業界経験者優遇',
        '丁寧で正確な作業ができる方'
      ],
      description: '契約書類の作成、顧客管理、電話応対などの事務業務全般を担当していただきます。',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20office%20worker%20woman%20at%20desk%20with%20computer%20and%20documents%2C%20organized%20workspace%2C%20efficient%20administrative%20work%2C%20modern%20office%20environment&width=500&height=300&seq=job2&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">採用情報</h1>
              <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                一緒に岡山の不動産業界を盛り上げませんか
              </p>
            </div>
          </div>
        </section>

        {/* Company Culture */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">私たちと一緒に働きませんか</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  おかやま不動産販売株式会社では、お客様に最高のサービスを提供するため、共に成長していける仲間を募集しています。
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  少数精鋭のチームだからこそ、一人ひとりの個性と能力を活かし、やりがいを持って働ける環境を整えています。未経験の方でも安心して始められる研修制度と、先輩スタッフによる丁寧なサポート体制があります。
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  地域に根ざした不動産会社として、お客様の人生の重要な決断をサポートする、責任とやりがいのある仕事です。
                </p>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20Japanese%20business%20team%20in%20modern%20office%2C%20diverse%20group%20of%20professionals%20smiling%2C%20collaborative%20work%20environment%2C%20positive%20workplace%20culture%2C%20team%20meeting%20atmosphere&width=600&height=400&seq=team-culture&orientation=landscape"
                  alt="チームワーク"
                  className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                働く環境・待遇
              </h2>
              <p className="text-xl text-gray-600">
                スタッフが安心して働ける環境づくりに取り組んでいます
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${benefit.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job Positions */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                募集職種
              </h2>
              <p className="text-xl text-gray-600">
                現在募集中のポジションをご紹介します
              </p>
            </div>

            <div className="space-y-12">
              {positions.map((position, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 p-8">
                      <div className="flex items-center mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mr-4">{position.title}</h3>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                          {position.type}
                        </span>
                      </div>
                      
                      <p className="text-lg text-gray-600 mb-6 leading-relaxed">{position.description}</p>
                      
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">給与</h4>
                        <p className="text-gray-700">{position.salary}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">応募資格</h4>
                        <ul className="space-y-2">
                          {position.requirements.map((req, reqIndex) => (
                            <li key={reqIndex} className="flex items-start">
                              <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                              <span className="text-gray-700">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="lg:col-span-1">
                      <img
                        src={position.image}
                        alt={position.title}
                        className="w-full h-full object-cover object-top min-h-64"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-20 bg-blue-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                応募の流れ
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'お問い合わせ', desc: 'お電話またはメールでご連絡ください' },
                { step: '02', title: '書類選考', desc: '履歴書・職務経歴書による選考' },
                { step: '03', title: '面接', desc: '代表との面接を実施します' },
                { step: '04', title: '内定・入社', desc: '条件面談後、入社日を決定' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              ご応募・お問い合わせ
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              ご質問やご相談がございましたら、お気軽にお問い合わせください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                086-012-3456
              </a>
              <a
                href="/#contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2"></i>
                メールで問い合わせ
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
